import { useMemo } from 'react';
import { useListUsersById } from '@shared/util/hooks';

interface UseResolveAssigneesResult {
  users: MBAPI.ShortUser[];
  loading: boolean;
}

/**
 * Resolves selected user IDs to user objects.
 * First looks for users in channelUsers, then fetches any missing users by ID.
 */
export function useResolveAssignees(
  selectedUserIds: number[],
  channelUsers: MBAPI.ShortUser[],
): UseResolveAssigneesResult {
  // Find users from channel that match selected IDs
  const channelSelectedUsers = useMemo(
    () => channelUsers.filter(user => selectedUserIds.includes(user.id)),
    [channelUsers, selectedUserIds],
  );

  // Find IDs that aren't in channel users (e.g., users who left the channel)
  const missingUserIds = useMemo(() => {
    const channelUserIds = channelUsers.map(user => user.id);
    return selectedUserIds.filter(id => !channelUserIds.includes(id));
  }, [channelUsers, selectedUserIds]);

  // Fetch missing users by ID
  const { users: fetchedUsers, loading } = useListUsersById(missingUserIds);

  // Combine channel users with fetched missing users
  const users = useMemo(
    () => [...channelSelectedUsers, ...fetchedUsers],
    [channelSelectedUsers, fetchedUsers],
  );

  return { users, loading };
}
